import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ObjectionStatsResponse, Period } from '@coachrex/shared';
import { useObjectionStats } from '../lib/api';
import { useRepFilter } from '../lib/repFilter';
import { scoreColor, stamp } from '../lib/format';
import { ErrorBox, RepFilter, Segmented, Skeleton } from '../components/ui';
import './rescue.css';

// Coaching → Objections: what leads push back on, across all analyzed calls (or one rep's).

type Row = ObjectionStatsResponse['rows'][number];

const PER: Record<Period, string> = { week: 'this week', month: 'in the last 30 days', quarter: 'in the last 90 days' };
const PREV: Record<Period, string> = { week: 'the week before', month: 'the 30 days before', quarter: 'the 90 days before' };

export function CoachingObjections({ period, periods, onPeriod }: { period: Period; periods: { value: Period; label: string }[]; onPeriod: (p: Period) => void }) {
  const rep = useRepFilter();
  const { data, error, isLoading } = useObjectionStats(period, rep.value === 'all' || rep.value === 'none' ? null : rep.value);
  const [open, setOpen] = useState<string | null>(null);

  const top = data?.rows[0];
  const maxShare = Math.max(1, ...(data?.rows.map((r) => r.share) ?? []));
  const raisedPct = data?.calls ? Math.round((100 * data.withObjection) / data.calls) : null;

  return (
    <div className="coach-main">
      <div className="obj-tools">
        <Segmented label="Period" options={periods} value={period} onChange={onPeriod} />
        <RepFilter reps={data?.reps ?? []} />
      </div>

      <div className="stat-cards">
        <Stat label="Calls analyzed" value={data ? data.calls.toLocaleString('en-US') : null} hint={data ? PER[period] : undefined} />
        <Stat
          label="Raised an objection"
          value={data ? (raisedPct == null ? '—' : `${raisedPct}%`) : null}
          hint={data ? `${data.withObjection.toLocaleString('en-US')} of ${data.calls.toLocaleString('en-US')} calls` : undefined}
        />
        <Stat label="Most common" value={data ? (top?.objection ?? '—') : null} hint={top ? `${top.share}% of calls · ${top.heard.toLocaleString('en-US')} calls` : undefined} small />
      </div>

      <section className="card stack-12">
        <div className="card-head">
          <span className="card-title">Most common objections</span>
          <span className="card-meta">Share of analyzed calls where the lead raised it, {PER[period]}</span>
        </div>
        {error ? (
          <ErrorBox error={error} />
        ) : isLoading || !data ? (
          [0, 1, 2, 3].map((i) => <Skeleton key={i} height={44} />)
        ) : data.calls === 0 ? (
          <span className="card-meta">No analyzed calls in this period. Objections show up here once calls are transcribed and analyzed.</span>
        ) : data.rows.length === 0 ? (
          <span className="card-meta">No objections raised on {data.calls.toLocaleString('en-US')} analyzed calls.</span>
        ) : (
          <div role="table" aria-label="Objections">
            <div role="row" className={`list-head objs-grid${data.judged ? '' : ' objs-grid--unjudged'}`} style={{ padding: '0 0 8px' }}>
              <span role="columnheader">Objection</span>
              <span role="columnheader">Share of calls</span>
              <span role="columnheader" style={{ textAlign: 'right' }} title={`Change in share against ${PREV[period]}`}>
                Change
              </span>
              <span role="columnheader">Trend</span>
              {data.judged && (
                <span role="columnheader" style={{ textAlign: 'right' }}>
                  Handled well
                </span>
              )}
              <span role="columnheader" style={{ textAlign: 'right' }}>
                Booked after
              </span>
            </div>
            {data.rows.map((r) => (
              <Fragment key={r.objection}>
                <button
                  role="row"
                  aria-expanded={open === r.objection}
                  className={`objs-grid objs-row${data.judged ? '' : ' objs-grid--unjudged'}${open === r.objection ? ' is-open' : ''}`}
                  onClick={() => setOpen(open === r.objection ? null : r.objection)}
                >
                  <span role="cell" className="objs-name">
                    <span className="objs-caret" aria-hidden>
                      ›
                    </span>
                    {r.objection}
                  </span>
                  <span role="cell" className="objs-share">
                    <span className="objs-share__track">
                      <span style={{ width: `${(100 * r.share) / maxShare}%` }} />
                    </span>
                    <b>{r.share}%</b>
                    <span className="card-meta" title={`${r.heard} calls`}>
                      {r.heard.toLocaleString('en-US')}
                    </span>
                  </span>
                  <span role="cell" className="objs-change">
                    <Change r={r} />
                  </span>
                  <span role="cell">
                    <Spark r={r} buckets={data.buckets} />
                  </span>
                  {data.judged && (
                    <span role="cell" className="objs-pct" style={{ color: scoreColor(r.handledPct) }}>
                      {r.handledPct}%
                    </span>
                  )}
                  <span role="cell" className="objs-pct">
                    {r.bookedPct}%
                  </span>
                </button>
                {open === r.objection && (
                  <div className="objs-detail">
                    <span className="overline">Latest calls where it came up</span>
                    {r.recent.map((c) => (
                      <div key={c.touchId} className="objs-call">
                        <span>
                          <b>{c.leadName}</b>
                          <span className="card-meta">
                            {' '}
                            · {c.rep ? `with ${c.rep}` : 'Nobody assigned'} · {stamp(c.at, data.timeZone)}
                          </span>
                        </span>
                        <span className={`objs-outcome${c.booked ? ' is-booked' : ''}`}>{c.booked ? 'Next step booked' : 'No next step'}</span>
                        <Link to={`/conversations/${c.leadId}?call=${c.touchId}`}>Review call →</Link>
                      </div>
                    ))}
                  </div>
                )}
              </Fragment>
            ))}
          </div>
        )}
        {data && data.rows.length > 0 && (
          <span className="card-meta">
            {data.judged && `${data.handledRule} `}Booked after = the call ended with a next step booked.
            {data.unheard.length > 0 && ` Not raised ${PER[period]}: ${data.unheard.join(', ')}.`}
          </span>
        )}
      </section>
    </div>
  );
}

/** Change in share against the previous period, in percentage points. Neutral ink: more objections isn't a rep's fault. */
function Change({ r }: { r: Row }) {
  if (r.prevShare == null) return <span className="card-meta">—</span>;
  const d = r.share - r.prevShare;
  if (d === 0) return <span className="card-meta">no change</span>;
  return (
    <span title={`${r.prevShare}% → ${r.share}%`}>
      {d > 0 ? '▲' : '▼'} {Math.abs(d)} pts
    </span>
  );
}

/** One small bar per bucket: the share of that bucket's calls where it came up. */
function Spark({ r, buckets }: { r: Row; buckets: ObjectionStatsResponse['buckets'] }) {
  const shares = buckets.map((b, i) => (b.calls ? r.series[i]! / b.calls : null));
  const max = Math.max(0.0001, ...shares.map((s) => s ?? 0));
  return (
    <span className="spark" aria-label={`Trend for ${r.objection}`}>
      {buckets.map((b, i) => {
        const s = shares[i];
        const tip = b.calls ? `${b.label}: ${r.series[i]} of ${b.calls} calls (${Math.round(100 * s!)}%)` : `${b.label}: no analyzed calls`;
        return (
          <span key={i} className="spark__hit" title={tip}>
            <span className={s == null ? 'is-empty' : undefined} style={{ height: s == null ? 2 : `${Math.max(8, (100 * s) / max)}%` }} />
          </span>
        );
      })}
    </span>
  );
}

function Stat({ label, value, hint, small }: { label: string; value: string | null; hint?: string; small?: boolean }) {
  return (
    <section className="card stat-card">
      <span className="stat-card__label">{label}</span>
      {value == null ? <Skeleton height={36} width={140} /> : <span className={`stat-card__value${small ? ' stat-card__value--text' : ''}`}>{value}</span>}
      {hint && <span className="card-meta">{hint}</span>}
    </section>
  );
}
